/**
 * Looks up a map of locale tags using the BPC 47 fallback algorithm.
 */
export function lookupLocaleKey<Value>(
    options: Record<string, Value>,
    match: string | string[],
): Value | undefined {
    const key = lookupLocale(Object.keys(options), match);
    if (key) {
        return options[key];
    }
}

/**
 * Implements the BCP 47 basic filtering algorithm. Returns every tag in options
 * that is the same as, or more specific than, any of the given language ranges.
 * Results are ordered by range priority first, then by options order, and
 * deduplicated.
 */
export function filterLocale(options: readonly string[], match: string | string[]): string[] {
    const ranges = Array.isArray(match) ? match : [match];
    const result: string[] = [];

    for (const range of ranges) {
        if (!range) {
            continue;
        }
        for (const option of options) {
            if (result.includes(option)) {
                continue;
            }
            if (range === '*' || option === range || option.startsWith(`${range}-`)) {
                result.push(option);
            }
        }
    }
    return result;
}

/**
 * Gets the name of the given language in the given display language. Default to
 * the currently active locale if no display language is given.
 */
export function getLanguageName(language: string, displayLanguage?: string): string | undefined {
    const names = new Intl.DisplayNames(displayLanguage, { type: 'language' });
    return names.of(language);
}

/**
 * Implements the BCP 47 lookup algorithm.
 */
export function lookupLocale<T extends string>(
    options: readonly T[],
    match: string | string[],
): T | undefined {
    if (Array.isArray(match)) {
        for (const tag of match) {
            const locale = lookupLocale(options, tag);
            if (locale) {
                return locale;
            }
        }
        return;
    }
    let current = match;
    for (;;) {
        if ((options as readonly string[]).includes(current)) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            return current as T;
        }
        const index = current.lastIndexOf('-');
        if (index === -1) {
            return;
        }
        current = current.slice(0, index);
    }
}
